import React, { useState } from 'react';
import { Alert, Box, Pagination, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { EmptySectionMessage, FullPageLoader } from '@tassomutuo-backoffice/ui';
import {
  ConsultationRequest,
  ConsultationRequestConsultantScope,
  PaginatedResponse,
} from '@tassomutuo-backoffice/types';
import { listConsultationRequests } from '../../../services/api/consultation-requests/consultation-requests';
import { useConsultationRequestsParams } from './hooks/useConsultationRequestsParams';
import ConsultationRequestsFiltersBar from './components/ConsultationRequestsFiltersBar';
import ConsultationRequestsSortSelect from './components/ConsultationRequestsSortSelect';
import ConsultationRequestCard from './components/ConsultationRequestCard';
import ConsultationRequestDetailModal from './components/ConsultationRequestDetailModal';

const QUERY_KEY = 'consultationRequests';

interface IProps {
  consultantScope?: ConsultationRequestConsultantScope;
}

const ConsultationRequestsList = ({ consultantScope }: IProps) => {
  const {
    state,
    apiParams,
    setQ,
    setServiceType,
    setCreatedFrom,
    setCreatedTo,
    setSortBy,
    setPage,
    resetFilters,
  } = useConsultationRequestsParams(consultantScope);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { isPending, data, error, isFetching } = useQuery<
    PaginatedResponse<ConsultationRequest>
  >({
    queryKey: [QUERY_KEY, apiParams],
    queryFn: () => listConsultationRequests(apiParams),
    refetchOnWindowFocus: false,
  });

  const results = data?.results ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalResults = data?.totalResults ?? 0;

  return (
    <Box>
      <FullPageLoader show={isPending} />

      <ConsultationRequestsFiltersBar
        q={state.q}
        serviceType={state.serviceType}
        createdFrom={state.createdFrom}
        createdTo={state.createdTo}
        onChangeQ={setQ}
        onChangeServiceType={setServiceType}
        onChangeCreatedFrom={setCreatedFrom}
        onChangeCreatedTo={setCreatedTo}
        onReset={resetFilters}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          pt: 3,
          pb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {data
            ? `${totalResults} richieste totali${
                isFetching ? ' · aggiornamento…' : ''
              }`
            : ' '}
        </Typography>
        <ConsultationRequestsSortSelect
          value={state.sortBy}
          onChange={setSortBy}
        />
      </Box>

      {error && (
        <Box px={4} pb={2}>
          <Alert severity="error">
            {(error as { message?: string })?.message
              ? `Errore: ${(error as { message?: string }).message}`
              : 'Si è verificato un errore durante il caricamento.'}
          </Alert>
        </Box>
      )}

      {!isPending && !error && results.length === 0 && (
        <EmptySectionMessage title="Nessuna richiesta di consulenza trovata con i filtri attuali" />
      )}

      <Stack>
        {results.map((request) => (
          <ConsultationRequestCard
            key={request.id}
            request={request}
            onOpenDetails={setSelectedId}
          />
        ))}
      </Stack>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <Pagination
            page={state.page}
            count={totalPages}
            color="primary"
            onChange={(_, value) => setPage(value)}
          />
        </Box>
      )}

      <ConsultationRequestDetailModal
        open={!!selectedId}
        requestId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </Box>
  );
};

export default ConsultationRequestsList;
